/**
 * @file OrdersFilterBar.tsx
 * @description Фильтры реестра заказов (phase19.24 §14; phase19.31 — макет (а)).
 *
 * С 1280 — одна строка: поиск по номеру · пилюли «Мой ход / Все» (SC45, не
 * кнопки) · «Стадия ▾» · «Источник ▾» · «Только открытые» (на 1024–1279
 * списки переносятся под поиск). До 1024 — поиск,
 * пилюли и кнопка «Фильтры · N» со шторкой: в строку телефона три выпадающих
 * списка не помещаются.
 *
 * «Стадия» — отбор по `stageId` (SC49) вместо прежнего фильтра по старым
 * статусам: в строке реестра показана стадия, и фильтр обязан говорить на
 * том же языке. Поиск применяется сам через полсекунды после ввода (и сразу
 * по Enter) — без кнопки «Применить».
 */

import { useEffect, useState } from "react";

import { FilterChips, FilterToggleChips, type FilterChip } from "../ui/FilterChips";
import { FilterSheetButton } from "../ui/FilterSheetButton";
import { SearchInput } from "../ui/SearchInput";
import { SelectField, type SelectFieldOption } from "../ui/SelectField";
import { BELOW_LG_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";
import { ORDER_SOURCE_LABEL, type OrderSource } from "../../lib/api-orders";
import {
  sheetFilterCount,
  type OrdersOwnerFilter,
  type OrdersRegistryQuery,
} from "../../lib/order-registry-query";

const OWNER_CHIPS: FilterChip<OrdersOwnerFilter>[] = [
  { value: "me", label: "Мой ход" },
  { value: "all", label: "Все" },
];

const SOURCE_OPTIONS: SelectFieldOption[] = [
  { value: "", label: "Все источники" },
  ...(Object.entries(ORDER_SOURCE_LABEL) as [OrderSource, string][]).map(([value, label]) => ({ value, label })),
];

/** Задержка поиска: не дёргаем сервер на каждую букву номера. */
const SEARCH_DELAY_MS = 400;

interface Props {
  query: OrdersRegistryQuery;
  onChange: (patch: Partial<OrdersRegistryQuery>) => void;
  stageOptions: SelectFieldOption[];
}

export function OrdersFilterBar({ query, onChange, stageOptions }: Props) {
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const [search, setSearch] = useState(query.search);

  // Адрес поменялся извне («Сбросить фильтры», «назад») — поле за ним.
  useEffect(() => setSearch(query.search), [query.search]);

  useEffect(() => {
    if (search.trim() === query.search) return;
    const timer = window.setTimeout(() => onChange({ search: search.trim() }), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search, query.search, onChange]);

  const selects = (
    <>
      <SelectField
        ariaLabel="Стадия заказа"
        value={query.stageId}
        onChange={(stageId) => onChange({ stageId })}
        options={stageOptions}
        placeholder="Все стадии"
        className={compact ? "w-full" : "w-[160px]"}
      />
      <SelectField
        ariaLabel="Источник заказа"
        value={query.source}
        onChange={(source) => onChange({ source: source as "" | OrderSource })}
        options={SOURCE_OPTIONS}
        placeholder="Все источники"
        className={compact ? "w-full" : "w-[152px]"}
      />
      <FilterToggleChips
        aria-label="Только открытые заказы"
        items={[{ key: "open", label: "Только открытые", pressed: query.openOnly, onToggle: () => onChange({ openOnly: !query.openOnly }) }]}
      />
    </>
  );

  const searchForm = (
    <form
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        onChange({ search: search.trim() });
      }}
    >
      <SearchInput value={search} onChange={setSearch} placeholder="Номер заказа" ariaLabel="Поиск по номеру заказа" />
    </form>
  );
  const ownerChips = (
    <FilterChips items={OWNER_CHIPS} value={query.owner} onChange={(owner) => onChange({ owner })} aria-label="Чей ход" />
  );

  if (compact) {
    return (
      <div className="flex flex-col gap-3">
        {searchForm}
        <div className="flex items-center justify-between gap-2">
          {ownerChips}
          <FilterSheetButton count={sheetFilterCount(query)} onReset={() => onChange({ stageId: "", source: "", openOnly: false })}>
            {selects}
          </FilterSheetButton>
        </div>
      </div>
    );
  }

  // Широкий экран: своя строка, а не `FilterBar` — у того поиск не уже 280px
  // и шире 480px, и на 1280 списки уезжали на вторую строку (макет (а) —
  // одна строка). Поиск занимает остаток (240–360px), списки — справа; на
  // 1024–1279 они переносятся под поиск.
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-0 max-w-[360px] flex-[1_1_240px]">{searchForm}</div>
      {ownerChips}
      <div className="ml-auto flex shrink-0 items-center gap-2">{selects}</div>
    </div>
  );
}
